import { makeObservable, observable, action, computed, runInAction } from 'mobx';
import {
  fetchSpendReport,
  fetchAgentSpend,
  fetchRunSpend,
  downloadSpendCsv,
  type AiSpendReport,
  type AiSpendAgentDetail,
  type AiSpendRunDetail,
  type AiSpendQuery,
} from '@/api/aiSpend';
import type { RootStore } from './RootStore';

// Agent Studio → Costs. A platform page: the merchant is an explicit filter
// (null = every merchant), independent of the header's selected merchant.
// Ranges are UTC calendar days — the API stores and buckets in UTC.

export type CostsRange = '7' | '30' | '90' | 'month' | 'custom';
export type CostsGrain = 'day' | 'week' | 'month';

/** Today as a UTC calendar day, "yyyy-MM-dd". */
export function utcToday(): string {
  return new Date().toISOString().slice(0, 10);
}

/** A UTC calendar day moved by whole days. Works on the string, so no local zone can shift it. */
export function addUtcDays(day: string, delta: number): string {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + delta)).toISOString().slice(0, 10);
}

export class CostsStore {
  @observable range: CostsRange = '30';
  @observable customFrom: string | null = null;
  @observable customTo: string | null = null;
  @observable merchantId: number | null = null;
  @observable grain: CostsGrain = 'day';

  @observable.ref report: AiSpendReport | null = null;
  @observable loading = false;
  @observable error: string | null = null;
  @observable exporting = false;

  @observable.ref agentDetail: AiSpendAgentDetail | null = null;
  @observable agentLoading = false;
  @observable agentError: string | null = null;

  @observable.ref runDetail: AiSpendRunDetail | null = null;
  @observable runLoading = false;
  @observable runError: string | null = null;

  /** Only the newest request may land — a slow 90-day answer must not overwrite a newer 7-day one. */
  private seq = 0;

  constructor(_root: RootStore) {
    void _root;
    makeObservable(this);
  }

  @computed get query(): AiSpendQuery {
    const today = utcToday();
    switch (this.range) {
      case '7': return { from: addUtcDays(today, -6), to: today, merchantId: this.merchantId };
      case '90': return { from: addUtcDays(today, -89), to: today, merchantId: this.merchantId };
      case 'month': return { from: today.slice(0, 8) + '01', to: today, merchantId: this.merchantId };
      case 'custom': return { from: this.customFrom ?? undefined, to: this.customTo ?? undefined, merchantId: this.merchantId };
      default: return { from: addUtcDays(today, -29), to: today, merchantId: this.merchantId };
    }
  }

  @action setRange(range: CostsRange): void {
    this.range = range;
    void this.load();
  }

  /** Show the date picker; nothing reloads until both dates are chosen. */
  @action pickCustom(): void {
    this.range = 'custom';
  }

  @action setCustom(from: string, to: string): void {
    this.range = 'custom';
    this.customFrom = from;
    this.customTo = to;
    void this.load();
  }

  @action setMerchant(merchantId: number | null): void {
    this.merchantId = merchantId;
    void this.load();
  }

  @action setGrain(grain: CostsGrain): void {
    this.grain = grain;
  }

  @action
  async load(): Promise<void> {
    const mine = ++this.seq;
    this.loading = true;
    this.error = null;
    try {
      const report = await fetchSpendReport(this.query);
      if (mine !== this.seq) return;
      runInAction(() => {
        this.report = report;
        this.loading = false;
      });
    } catch (err) {
      if (mine !== this.seq) return;
      runInAction(() => {
        this.loading = false;
        this.error = err instanceof Error ? err.message : 'Costs could not be loaded';
      });
    }
  }

  @action
  async openAgent(agentId: number | null): Promise<void> {
    this.agentDetail = null;
    this.agentError = null;
    this.agentLoading = true;
    try {
      const detail = await fetchAgentSpend(this.query, agentId);
      runInAction(() => {
        this.agentDetail = detail;
        this.agentLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.agentLoading = false;
        this.agentError = err instanceof Error ? err.message : 'This agent could not be loaded';
      });
    }
  }

  @action closeAgent(): void {
    this.agentDetail = null;
    this.agentError = null;
    this.agentLoading = false;
  }

  @action
  async openRun(runKey: string, focusAgentId?: number | null): Promise<void> {
    this.runDetail = null;
    this.runError = null;
    this.runLoading = true;
    try {
      const run = await fetchRunSpend(runKey, focusAgentId);
      runInAction(() => {
        this.runDetail = run;
        this.runLoading = false;
      });
    } catch (err) {
      runInAction(() => {
        this.runLoading = false;
        this.runError = err instanceof Error ? err.message : 'This run could not be loaded';
      });
    }
  }

  @action closeRun(): void {
    this.runDetail = null;
    this.runError = null;
    this.runLoading = false;
  }

  @action
  async exportCsv(): Promise<void> {
    this.exporting = true;
    try {
      await downloadSpendCsv(this.query);
    } finally {
      runInAction(() => {
        this.exporting = false;
      });
    }
  }
}
